import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { TENANT } from "../constants/testIds";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Buildings, Trash } from "@phosphor-icons/react";

export default function AgencyTenants() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", address: "" });
  const [err, setErr] = useState("");

  const load = () => api.get("/tenants").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setErr("");
    try { await api.post("/tenants", form); toast.success("Company created"); setOpen(false); setForm({ name: "", slug: "", address: "" }); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const del = async (t) => {
    const typed = window.prompt(
      `This permanently deletes "${t.name}" and ALL its data (employees, payroll, leaves, everything). This cannot be undone.\n\nType the company name to confirm:`
    );
    if (typed == null) return;
    if (typed.trim() !== t.name) { toast.error("Name did not match — deletion cancelled."); return; }
    try {
      await api.delete(`/tenants/${t.id}`);
      toast.success(`"${t.name}" deleted`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Agency</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Client companies</h1>
          <p className="text-sm text-zinc-500 mt-2">{rows.length} tenants under your agency umbrella.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-zenith rounded-sm" data-testid={TENANT.createBtn}><Plus size={16} className="mr-1.5" /> New company</Button>
          </DialogTrigger>
          <DialogContent data-testid={TENANT.createDialog}>
            <DialogHeader><DialogTitle className="font-display text-2xl">Onboard a client company</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1"><Label className="text-xs">Company name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="tenant-name-input" /></div>
              <div className="col-span-2 space-y-1"><Label className="text-xs">Slug (unique URL)</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} className="rounded-sm font-mono-plex" data-testid="tenant-slug-input" /></div>
              <div className="col-span-2 space-y-1"><Label className="text-xs">Registered address</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-sm" data-testid="tenant-address-input" /></div>
            </div>
            {err && <div className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded-sm">{err}</div>}
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={submit} className="btn-zenith rounded-sm" data-testid="tenant-save-btn">Create company</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={TENANT.list}>
        {rows.map((t) => (
          <div key={t.id} className="border border-zinc-200 bg-white rounded-sm p-5" data-testid={`tenant-card-${t.slug}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-700 rounded-sm grid place-items-center">
                <Buildings size={20} weight="duotone" className="text-white" />
              </div>
              <div>
                <div className="font-display text-lg font-bold">{t.name}</div>
                <div className="text-xs font-mono-plex text-zinc-500">/{t.slug}</div>
              </div>
            </div>
            {t.address && <div className="mt-3 text-xs text-zinc-600">{t.address}</div>}
            <div className="mt-4 pt-3 border-t border-zinc-100 flex items-center justify-between">
              <span className="text-[11px] text-zinc-500">Created {new Date(t.created_at).toLocaleDateString()}</span>
              <button onClick={() => del(t)} data-testid={`tenant-delete-${t.slug}`}
                className="text-[11px] inline-flex items-center gap-1 text-red-600 hover:text-red-700 hover:underline">
                <Trash size={13} /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
